import { useLabResult } from '@hook'
import LabResultDocumentPreview from '@pages/LabTech/LabResultDocumentPreview'
import type { LabOrderIncludedLabResult } from '@type/lab.type'
import { ModalHeader } from '@ui'
import { Box, Form, Layer, Spinner, Text } from 'grommet'
import moment from 'moment'
import type { FC } from 'react'

type Props = {
  isOpen?: boolean
  onClose?: () => void
  defaultData?: LabOrderIncludedLabResult | null
  orderId?: string
  resultId?: string
}

const LabResultPreview: FC<Props> = ({ isOpen = true, onClose = () => {}, defaultData, orderId, resultId }) => {
  const shouldFetch = !defaultData && resultId
  const { labResult, isLabResultLoading } = useLabResult(orderId, resultId)

  const preview = defaultData ?? (labResult?.data as LabOrderIncludedLabResult | undefined)

  const label = (r?: LabOrderIncludedLabResult | null) => {
    if (!r) return ''
    const doc = r.attributes.document
    if (doc?.name) return doc.name
    const t = r.attributes.created_at
    return t ? `Lab result · ${moment(t).format('MMM D, YYYY h:mm A')}` : `Lab result · #${r.id}`
  }

  return (
    <Box>
      <Layer
        modal
        onEsc={onClose}
        position="center"
        style={{ borderColor: '#999999 !important', borderRadius: '15px' }}
      >
        <Form>
          <Box style={{ borderRadius: '2em', maxHeight: '90vh', overflowY: 'auto', width: '81vw' }}>
            <ModalHeader onClose={onClose} title="Lab Result Preview" />
            <Box pad={{ horizontal: 'large', vertical: 'medium' }}>
              {shouldFetch && isLabResultLoading ? (
                <Box align="center" justify="center" pad="large">
                  <Spinner size="medium" /> Loading...
                </Box>
              ) : preview ? (
                <>
                  {preview.attributes.document ? (
                    <LabResultDocumentPreview document={preview.attributes.document} />
                  ) : preview.attributes.components?.length ? (
                    <div className="overflow-x-auto rounded border border-[#D9D9D9]">
                      <table className="w-full border-collapse text-sm">
                        <thead>
                          <tr className="border-b border-[#D9D9D9] bg-[#fafafa]">
                            <th className="px-3 py-2 text-left text-xs font-semibold text-[#555]">Test</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-[#555]">Value</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-[#555]">Unit</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-[#555]">Ref</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.attributes.components.map((c, i) => (
                            <tr className="border-b border-[#eee]" key={`${c.name ?? 'row'}-${i}`}>
                              <td className="px-3 py-2">{c.name ?? '—'}</td>
                              <td className="px-3 py-2">{c.value}</td>
                              <td className="px-3 py-2">{c.unit}</td>
                              <td className="px-3 py-2 text-xs text-[#666]">
                                {c.reference_min} – {c.reference_max}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <Text color="#666" size="small">
                      No preview data for this result.
                    </Text>
                  )}
                </>
              ) : (
                <Text>No lab result found.</Text>
              )}
            </Box>
          </Box>
        </Form>
      </Layer>
    </Box>
  )
}

export default LabResultPreview
